/**
 * SWR access to the logged-in user's server-backed MedChron export preference — read via
 * `GET /me/preference/medchron_export`, written via `PUT`. The write seeds the cache from its
 * own response, so no caller touches the SWR cache directly. Preferences are never read for
 * another user: an export attributed to the case rather than to the person triggering it (the
 * final export on mark-as-reviewed) is resolved server-side from the case physician's row.
 * The cache holds mapped UI state, not the raw response — `schema_version` rides along on
 * `MedChronExportUiState.schemaVersion`.
 */
import { useUserPreferencesService } from "@/hooks/useUserPreferencesService";
import {
  transformMedChronExportUiStateToPutRequest,
  transformResponseToMedChronExportUiState,
} from "@/services/userPreferencesService.mapper";
import type { MedChronExportUiState } from "@/services/userPreferencesService.types";
import { getErrorMessage } from "@/utils/errorUtils";
import { useCallback, useState } from "react";
import useSWR, { mutate } from "swr";

// Collapse duplicate fetches of the same key within this window; combined with
// revalidateOnFocus/Reconnect:false below, prefs are fetched at most once per session.
const EXPORT_PREFS_DEDUPING_INTERVAL_MS = 30 * 60 * 1000;

export const myExportPrefsKey = () => ["/me", "preference", "medchron_export"] as const;

/** Logged-in user's saved MedChron export preferences via `GET /me/preference/medchron_export`. */
export function useMyExportPreferences(enabled = true) {
  const userPreferencesService = useUserPreferencesService();

  const {
    data: uiState,
    error,
    isLoading,
    isValidating,
  } = useSWR(
    enabled ? myExportPrefsKey() : null,
    () =>
      userPreferencesService
        .getMyMedChronExportPreferences()
        .then(transformResponseToMedChronExportUiState),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: EXPORT_PREFS_DEDUPING_INTERVAL_MS,
    }
  );

  return {
    uiState,
    isLoading,
    isValidating,
    isError: Boolean(error),
    errorMessage: error ? getErrorMessage(error) : null,
  };
}

/**
 * Writes the logged-in user's MedChron export preferences via
 * `PUT /me/preference/medchron_export` and seeds the read cache from the response.
 *
 * @returns `saveMyExportPreferences`, which rejects on failure so the calling component decides
 *   what to show, and `isSaving` for the in-flight state.
 */
export function useSaveMyExportPreferences() {
  const userPreferencesService = useUserPreferencesService();
  const [isSaving, setIsSaving] = useState(false);

  const saveMyExportPreferences = useCallback(
    async (uiState: MedChronExportUiState): Promise<void> => {
      setIsSaving(true);
      try {
        const response = await userPreferencesService.putMyMedChronExportPreferences(
          transformMedChronExportUiStateToPutRequest(uiState)
        );
        await mutate(myExportPrefsKey(), transformResponseToMedChronExportUiState(response), {
          revalidate: false,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [userPreferencesService]
  );

  return { saveMyExportPreferences, isSaving };
}
